import React, { useState } from 'react';
import { 
  TrendingUp, Book, FileText, User, Clock, CheckCircle, Utensils, 
  DollarSign, BarChart, Award, Users, Star, Calendar, PieChart, 
  Zap, Target, BellRing, ArrowUp, ArrowDown, Share2, Facebook, 
  Instagram, Twitter, Linkedin, Globe, Info, MessageCircle,
  AlertCircle, Shield, PlusCircle,
  BriefcaseBusiness,
  CoffeeIcon,
  X
} from 'lucide-react';
import PolicyCard from './PolicyCard';

const MarketingTab = ({ 
  selectedTab, 
  marketingCampaigns = [], 
  trainingPrograms = [], 
  policies = {}, 
  changePolicy, 
  launchMarketingCampaign, 
  conductTraining, 
  money = 5000, 
  day = 1, 
  staff = [],
  reputation = 3.5, // Sample value for metrics display
  addNotification // Add this prop to make sure notifications work
}) => {
  // State for policy information modal
  const [showPolicyInfoModal, setShowPolicyInfoModal] = useState(false);
  const [selectedPolicyInfo, setSelectedPolicyInfo] = useState(null);
  
  // State for social share modal
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  // Calculate active campaigns
  const activeCampaigns = marketingCampaigns.filter(c => c.active).length;
  
  // Helper function to render policy impact badges
  const renderPolicyImpact = (type) => {
    const impacts = {
      'petFriendly': { family: 'positive', business: 'negative' },
      'earlyCheckin': { business: 'positive', leisure: 'positive' },
      'lateCheckout': { leisure: 'positive' },
      'freeCancellation': { all: 'positive', revenue: 'negative' },
      'breakfastIncluded': { all: 'positive', revenue: 'negative' },
      'pricingGuarantee': { all: 'positive', business: 'positive', revenue: 'negative' },
      'loyaltyProgram': { all: 'positive', business: 'positive', revenue: 'neutral' },
      'sustainabilityPledge': { leisure: 'positive', family: 'positive', revenue: 'neutral' }
    };
    
    const impact = impacts[type];
    if (!impact) return null;
    
    return (
      <div className="flex flex-wrap gap-1 mt-1">
        {impact.all && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 flex items-center">
            <Users className="w-3 h-3 mr-0.5" /> All guests
            {impact.all === 'positive' ? <ArrowUp className="w-3 h-3 ml-0.5 text-green-500" /> : 
             impact.all === 'negative' ? <ArrowDown className="w-3 h-3 ml-0.5 text-red-500" /> : 
             <span className="w-3 h-3 ml-0.5 text-gray-500">↔</span>}
          </span>
        )}
        {impact.business && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 flex items-center">
            <BriefcaseBusiness className="w-3 h-3 mr-0.5" /> Business
            {impact.business === 'positive' ? <ArrowUp className="w-3 h-3 ml-0.5 text-green-500" /> : 
             impact.business === 'negative' ? <ArrowDown className="w-3 h-3 ml-0.5 text-red-500" /> : 
             <span className="w-3 h-3 ml-0.5 text-gray-500">↔</span>}
          </span>
        )}
        {impact.family && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-green-50 text-green-700 flex items-center">
            <Users className="w-3 h-3 mr-0.5" /> Family
            {impact.family === 'positive' ? <ArrowUp className="w-3 h-3 ml-0.5 text-green-500" /> : 
             impact.family === 'negative' ? <ArrowDown className="w-3 h-3 ml-0.5 text-red-500" /> : 
             <span className="w-3 h-3 ml-0.5 text-gray-500">↔</span>}
          </span>
        )}
        {impact.leisure && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 flex items-center">
            <CoffeeIcon className="w-3 h-3 mr-0.5" /> Leisure
            {impact.leisure === 'positive' ? <ArrowUp className="w-3 h-3 ml-0.5 text-green-500" /> : 
             impact.leisure === 'negative' ? <ArrowDown className="w-3 h-3 ml-0.5 text-red-500" /> : 
             <span className="w-3 h-3 ml-0.5 text-gray-500">↔</span>}
          </span>
        )}
        {impact.revenue && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-red-50 text-red-700 flex items-center">
            <DollarSign className="w-3 h-3 mr-0.5" /> Revenue
            {impact.revenue === 'positive' ? <ArrowUp className="w-3 h-3 ml-0.5 text-green-500" /> : 
             impact.revenue === 'negative' ? <ArrowDown className="w-3 h-3 ml-0.5 text-red-500" /> : 
             <span className="w-3 h-3 ml-0.5 text-gray-500">↔</span>}
          </span>
        )}
      </div>
    );
  };
  
  // Marketing effectiveness indicator based on hotel reputation
  const getMarketingEffectiveness = () => {
    if (reputation >= 4.5) return { level: 'Excellent', color: 'green' };
    if (reputation >= 3.5) return { level: 'Good', color: 'blue' };
    if (reputation >= 2.5) return { level: 'Average', color: 'amber' };
    return { level: 'Poor', color: 'red' };
  };
  
  const marketingEffectiveness = getMarketingEffectiveness();
  
  // Function to handle policy info display
  const showPolicyDetails = (policyType) => {
    const policyDetails = {
      'petFriendly': {
        title: 'Pet Friendly Policy',
        description: 'This policy allows guests to bring their pets for an additional fee. It attracts families and leisure travelers but may deter some business guests who prefer a pet-free environment.',
        pros: ['Attracts pet owners', 'Additional revenue stream', 'Extended average stay length'],
        cons: ['Potential damage to rooms', 'Additional cleaning costs', 'May deter guests with allergies']
      },
      'earlyCheckin': {
        title: 'Early Check-in Policy',
        description: 'Allows guests to check in before the standard check-in time, typically for an additional fee or as a perk for loyalty program members.',
        pros: ['Improved guest satisfaction', 'Additional revenue if charged', 'Competitive advantage'],
        cons: ['Staffing challenges', 'Room availability issues', 'Operational pressure']
      },
      'lateCheckout': {
        title: 'Late Check-out Policy',
        description: 'Permits guests to check out after the standard check-out time, typically for an additional fee or as a loyalty perk.',
        pros: ['Guest convenience', 'Additional revenue if charged', 'Increased guest satisfaction'],
        cons: ['Housekeeping scheduling challenges', 'Potential room availability issues']
      },
      'freeCancellation': {
        title: 'Free Cancellation Policy',
        description: 'Allows guests to cancel their reservation without penalty up to a certain time before check-in.',
        pros: ['Increases booking confidence', 'Higher booking conversion rates', 'Competitive advantage'],
        cons: ['Revenue uncertainty', 'Last-minute cancellations', 'Potential for abuse']
      },
      'breakfastIncluded': {
        title: 'Breakfast Included Policy',
        description: 'Provides complimentary breakfast with all room bookings, enhancing the overall guest experience.',
        pros: ['Improved guest satisfaction', 'Higher perceived value', 'Increased food & beverage revenue'],
        cons: ['Additional operating costs', 'Staffing requirements', 'Food waste potential']
      },
      'pricingGuarantee': {
        title: 'Best Price Guarantee',
        description: 'Promise to match or beat any lower price found elsewhere for the same room and dates.',
        pros: ['Builds booking confidence', 'Encourages direct bookings', 'Competitive advantage'],
        cons: ['Potential revenue loss', 'Administrative burden', 'Price monitoring requirements']
      },
      'loyaltyProgram': {
        title: 'Loyalty Program',
        description: 'Rewards frequent guests with points, perks, or special offers based on their stays.',
        pros: ['Encourages repeat business', 'Customer data collection', 'Direct booking incentive'],
        cons: ['Program administration costs', 'Points liability', 'Reward fulfillment costs']
      },
      'sustainabilityPledge': {
        title: 'Sustainability Pledge',
        description: 'Commitment to environmentally friendly practices throughout hotel operations.',
        pros: ['Appeals to eco-conscious travelers', 'Potential cost savings', 'Brand enhancement'],
        cons: ['Implementation costs', 'Staff training needs', 'Certification expenses']
      }
    };
    
    setSelectedPolicyInfo(policyDetails[policyType]);
    setShowPolicyInfoModal(true);
  };

  const policyDefinitions = [
    { key: 'petFriendly', title: 'Pet Friendly', description: 'Allow guests to bring pets for an additional fee.', icon: User, color: 'green' },
    { key: 'earlyCheckin', title: 'Early Check-in', description: 'Allow guests to check in before the standard time.', icon: Clock, color: 'blue' },
    { key: 'lateCheckout', title: 'Late Check-out', description: 'Allow guests to check out after the standard time.', icon: Clock, color: 'purple' },
    { key: 'freeCancellation', title: 'Free Cancellation', description: 'Allow guests to cancel reservation without penalty.', icon: CheckCircle, color: 'amber' },
    { key: 'breakfastIncluded', title: 'Breakfast Included', description: 'Include complimentary breakfast with room bookings.', icon: Utensils, color: 'lime' },
    { key: 'pricingGuarantee', title: 'Best Price', description: 'Guarantee the best available rate for direct bookings.', icon: Shield, color: 'indigo' },
    { key: 'loyaltyProgram', title: 'Loyalty Program', description: 'Reward frequent guests with points and special perks.', icon: Award, color: 'purple' },
    { key: 'sustainabilityPledge', title: 'Sustainability', description: 'Commit to environmentally friendly practices.', icon: Globe, color: 'teal' },
  ];
  
  // Function to open social share modal
  const openSocialShare = (campaign) => {
    setSelectedCampaign(campaign);
    setShowSocialModal(true);
  };
  
  // Function to handle social media sharing
  const handleSocialShare = (platform) => {
    // In a real application, this would integrate with the social media APIs
    console.log(`Sharing on ${platform}: ${selectedCampaign?.name}`);
    
    // Add a notification for the social share
    if (typeof addNotification === 'function') {
      addNotification(`Campaign "${selectedCampaign?.name}" shared on ${platform}.`, false, "marketing");
    }
    
    setShowSocialModal(false);
  };
  
  // Handle policy change with proper notification
  const handlePolicyChange = (policyType, value) => {
    // If the changePolicy function exists, use it
    if (typeof changePolicy === 'function') {
      changePolicy(policyType, value);
    } else {
      console.warn('changePolicy function not available');
      // If no parent function, just log the change
      console.log(`Policy ${policyType} changed to ${value}`);
      
      // Create our own notification if parent doesn't provide one
      if (typeof addNotification === 'function') {
        // Get user-friendly policy name
        const policyNames = {
          petFriendly: "Pet Friendly",
          earlyCheckin: "Early Check-in",
          lateCheckout: "Late Check-out",
          freeCancellation: "Free Cancellation",
          breakfastIncluded: "Breakfast Included",
          pricingGuarantee: "Best Price Guarantee",
          loyaltyProgram: "Loyalty Program",
          sustainabilityPledge: "Sustainability Pledge"
        };
        
        const policyName = policyNames[policyType] || policyType;
        const statusText = value ? "enabled" : "disabled";
        
        addNotification(`${policyName} policy ${statusText}.`, false, "policy");
      }
    }
  };
  
  // Function to launch marketing campaign with notification
  const handleLaunchCampaign = (campaignId) => {
    // Call the parent function
    if (typeof launchMarketingCampaign === 'function') {
      launchMarketingCampaign(campaignId);
    } else {
      console.warn('launchMarketingCampaign function not available');
      
      // Find the campaign for the notification
      const campaign = marketingCampaigns.find(c => c.id === campaignId);
      
      // Create our own notification if parent doesn't provide one
      if (campaign && typeof addNotification === 'function') {
        addNotification(`Campaign "${campaign.name}" launched successfully.`, false, "marketing");
      }
    }
  };
  
  
  const XIcon = (props) => (
    <div {...props}>
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6 6 18"></path>
        <path d="m6 6 12 12"></path>
      </svg>
    </div>
  );
  
  return (
    selectedTab === 'marketing' && (
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
          <div>
            <h2 className="text-xl font-bold flex items-center">
              <TrendingUp className="mr-2 text-purple-600" />
              Marketing
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Boost your hotel's visibility and attract more guests
            </p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <div className="px-4 py-2 bg-gray-50 rounded-lg flex items-center">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-2">
                <Zap className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Active Campaigns</div>
                <div className="font-bold">{activeCampaigns} running</div>
              </div>
            </div>
            
            <div className="px-4 py-2 bg-gray-50 rounded-lg flex items-center">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                <Target className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Marketing Effectiveness</div>
                <div className="font-bold text-blue-600">
                  {marketingEffectiveness.level}
                </div>
              </div>
            </div>
            
            <div className="px-4 py-2 bg-gray-50 rounded-lg flex items-center">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-2">
                <DollarSign className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Budget Available</div>
                <div className="font-bold">${money}</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Marketing Campaigns with Social Sharing */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-purple-600" />
              Marketing Campaigns
            </h3>
            
            <div className="px-3 py-1 bg-purple-50 border border-purple-100 rounded-lg text-xs text-purple-800 flex items-center">
              <PieChart className="w-3 h-3 mr-1" />
              Campaign ROI: {reputation >= 3 ? 'High' : 'Moderate'}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {marketingCampaigns.map(campaign => (
              <div key={campaign.id} 
                className={`border rounded-lg overflow-hidden shadow-sm transition-all hover:shadow 
                ${campaign.active ? 'bg-gradient-to-br from-purple-50 to-white border-purple-200' : 'bg-white hover:border-purple-200'}`}>
                <div className="p-4 border-b bg-gradient-to-r from-purple-50 to-white">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold flex items-center">
                        {campaign.active && <BellRing className="w-4 h-4 mr-1 text-purple-600" />}
                        {campaign.name}
                      </h4>
                      <div className="flex flex-wrap mt-1 gap-1">
                        {campaign.targetAudience?.map(audience => (
                          <span key={audience} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
                            {audience}
                          </span>
                        ))}
                      </div>
                    </div>
                    {campaign.active && (
                      <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full font-medium flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {campaign.endDay - day} days left
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="p-4">
                  <p className="text-sm mb-4 text-gray-600">{campaign.description}</p>
                  
                  <div className="flex justify-between mb-4 text-xs text-gray-600">
                    <div className="flex items-center">
                      <DollarSign className="w-4 h-4 mr-1 text-green-600" />
                      <span>${campaign.cost}</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1 text-blue-600" />
                      <span>{campaign.duration} days</span>
                    </div>
                    <div className="flex items-center">
                      <Target className="w-4 h-4 mr-1 text-purple-600" />
                      <span>+{campaign.reputationBoost || '?'} reputation</span>
                    </div>
                  </div>
                  
                  {campaign.active ? (
                    <div className="space-y-2">
                      <div className="bg-purple-50 rounded-lg p-2 flex items-center justify-between">
                        <div className="text-xs text-purple-800">
                          <span className="font-medium">Campaign in progress</span>
                          <div className="mt-1">Ends on Day {campaign.endDay}</div>
                        </div>
                        <div className="h-2 bg-purple-100 rounded-full w-20 overflow-hidden">
                          <div 
                            className="h-full bg-purple-600 rounded-full" 
                            style={{ width: `${((campaign.endDay - day) / campaign.duration) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <button
                        onClick={() => handleLaunchCampaign(campaign.id)}
                        disabled={campaign.active || (money+5000) < campaign.cost}
                        className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors
                          ${campaign.active || (money+5000) < campaign.cost
                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-purple-600 to-purple-500 text-white hover:from-purple-500 hover:to-purple-400 shadow-sm hover:shadow'
                          }`}
                      >
                        {(money+5000) < campaign.cost ? (
                          <span className="flex items-center justify-center">
                            <DollarSign className="w-4 h-4 mr-1" />
                            Insufficient funds
                          </span>
                        ) : (
                          <span className="flex items-center justify-center">
                            <Zap className="w-4 h-4 mr-1" />
                            Launch Campaign
                          </span>
                        )}
                      </button>

                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div>
      {/* Section Header (Same as before) */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2">
        <h3 className="text-lg font-semibold flex items-center text-black">
          <FileText className="w-5 h-5 mr-2 text-black" />
          Hotel Policies
        </h3>
      </div>

      {/* Policies Grid Container */}
      <div className="p-3 sm:p-5 rounded-lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">

          {/* Map over policy definitions to render cards */}
          {policyDefinitions.map((policy) => (
            <PolicyCard
              key={policy.key} // Essential for React lists
              policyKey={policy.key}
              title={policy.title}
              description={policy.description}
              icon={policy.icon}
              isActive={policies[policy.key]} // Get current state
              onToggle={handlePolicyChange} // Pass handler
              onShowDetails={showPolicyDetails} // Pass handler
              renderImpact={renderPolicyImpact} // Pass rendering function
              activeColor={policy.color} // Pass the color name
            />
          ))}

        </div> {/* End Grid */}
      </div> {/* End Policies Container */}
    </div>
        
        {/* Policy Information Modal */}
        {showPolicyInfoModal && selectedPolicyInfo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 m-4">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-bold">{selectedPolicyInfo.title}</h3>
                <button 
                  onClick={() => setShowPolicyInfoModal(false)}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <XIcon className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <p className="text-gray-600 mb-4">{selectedPolicyInfo.description}</p>
              
              <div className="mb-4">
                <h4 className="font-medium text-green-700 flex items-center mb-2">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Benefits
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {selectedPolicyInfo.pros.map((pro, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                        <span className="text-green-600 text-xs">+</span>
                      </div>
                      {pro}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="mb-4">
                <h4 className="font-medium text-red-700 flex items-center mb-2">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  Considerations
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {selectedPolicyInfo.cons.map((con, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center mr-2 mt-0.5">
                        <span className="text-red-600 text-xs">-</span>
                      </div>
                      {con}
                    </li>
                  ))}
                </ul>
              </div>
              
              <button
                onClick={() => setShowPolicyInfoModal(false)}
                className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        )}
        
        {/* Social Media Share Modal */}
        {showSocialModal && selectedCampaign && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 m-4">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-bold">Share on Social Media</h3>
                <button 
                  onClick={() => setShowSocialModal(false)}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <XIcon className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <p className="text-gray-600 mb-4">
                Share the "{selectedCampaign.name}" campaign on your hotel's social media channels to increase its reach.
              </p>
              
              <div className="grid grid-cols-2 gap-3 mb-5">
                <button
                  onClick={() => handleSocialShare('Facebook')}
                  className="p-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center"
                >
                  <Facebook className="w-5 h-5 mr-2" />
                  Facebook
                </button>
                
                <button
                  onClick={() => handleSocialShare('Twitter')}
                  className="p-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center"
                >
                  <Twitter className="w-5 h-5 mr-2" />
                  Twitter
                </button>
                
                <button
                  onClick={() => handleSocialShare('Instagram')}
                  className="p-3 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors flex items-center justify-center"
                >
                  <Instagram className="w-5 h-5 mr-2" />
                  Instagram
                </button>
                
                <button
                  onClick={() => handleSocialShare('LinkedIn')}
                  className="p-3 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors flex items-center justify-center"
                >
                  <Linkedin className="w-5 h-5 mr-2" />
                  LinkedIn
                </button>
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <h4 className="font-medium text-sm mb-2">Suggested Post</h4>
                <p className="text-sm text-gray-600">
                  Check out our latest promotion: {selectedCampaign.name}! {selectedCampaign.description} Book now to take advantage of this special offer.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  );
};

export default MarketingTab;